import get from "lodash/get"
import { observable, computed, action } from "mobx"
import { firebase, imagesFirebase } from "../constants"
import { functionsUrl } from "./../constants"
import { productStore, storefrontStore } from "../stores"
import defaultStoreLogo from "../assets/default.png"

class AuthStore {
  constructor() {
    this.listenToAuth()
  }
  @observable authenticated = false
  @observable authenticating = false
  @observable user = null
  @observable signUpError = null
  @observable signInError = null
  @observable store = null
  @observable loading = true
  @observable recentStorePurchases = observable.map()
  @observable purchases = observable.map()
  @observable storePurchaseTotals = observable.map()
  @observable storePurchaseProducts = observable.map()

  @computed
  get last30DaysProfit() {
    return this.recentStorePurchases.keys().reduce((acc, currentId) => {
      var purchase = this.purchases.get(currentId)
      if (purchase) {
        acc += purchase.sellerProfit
      }
      return acc
    }, 0)
  }

  @computed
  get donateEnabled() {
    if (
      this.store &&
      this.store.config &&
      this.store.config.donate &&
      this.store.config.donate.active
    ) {
      return true
    }
    return false
  }

  @computed
  get signUpErrorMessage() {
    return this.signUpError ? this.signUpError.message : ""
  }
  @computed
  get signInErrorMessage() {
    return this.signInError ? this.signInError.message : ""
  }

  @computed
  get userId() {
    return this.user.data.uid
  }

  @computed
  get userStoreId() {
    return Object.keys(this.user.stores)[0]
  }

  @computed
  get userStoreLogo() {
    return get(this.store, "config.logo", defaultStoreLogo)
  }

  @computed
  get authUser() {
    return firebase.auth().currentUser
  }

  @action.bound
  uploadProductImage(e) {
    const uploadsRef = firebase
      .database()
      .ref("users")
      .child(this.userId)
      .child("uploads")

    const storeUploadsRef = firebase
      .database()
      .ref("stores")
      .child(this.userStoreId)
      .child("uploads")
    const uploadKey = uploadsRef.push().key

    let file = e.target.files[0]
    console.log(file)

    return new Promise((resolve, reject) => {
      if (!uploadKey || !this.userId) {
        reject()
        return
      }
      var storageRef = imagesFirebase
        .ref()
        .child("images")
        .child("users")
        .child(this.userId)
        .child(uploadKey)
      storageRef
        .put(file, {
          cacheControl: "public,max-age=3000",
          //contentType: "image/png",
          name: uploadKey,
        })
        .then(snapshot => {
          var publicUrl =
            "https://storage.googleapis.com/tinymerch-36183.appspot.com/images/users/" +
            this.userId +
            "/" +
            uploadKey
          var customUrl =
            "https://images.tinymerch.com/images/users/" +
            this.userId +
            "/" +
            uploadKey

          const payload = {
            customUrl: customUrl,
            publicUrl: publicUrl,
            dateCreated: Date.now(),
          }

          uploadsRef.child(uploadKey).set(payload)
          storeUploadsRef.child(uploadKey).set(payload)

          resolve(payload)
        })
    })
  }

  @action.bound
  stripeTransfers() {
    firebase
      .auth()
      .currentUser.getToken(false)
      .then(function(idToken) {
        window.location.href = functionsUrl + "stripeTransfers?token=" + idToken
      })
  }
  @action.bound
  stripeAuthorize() {
    firebase
      .auth()
      .currentUser.getToken(false)
      .then(function(idToken) {
        window.location.href = functionsUrl + "stripeAuthorize?token=" + idToken
      })
  }

  @action.bound
  loadUser(user) {
    this.authenticated = true
    firebase
      .database()
      .ref(`users/${user.uid}`)
      .once("value")
      .then(
        action(snap => {
          this.user = snap.val()
          this.loadStore()
          productStore.loadProducts()
        })
      )
  }

  @action.bound
  toggleDonate() {
    firebase
      .database()
      .ref(`stores/${this.userStoreId}/config/donate/active`)
      .set(!this.donateEnabled)
  }

  @action.bound
  loadStore() {
    firebase
      .database()
      .ref("stores")
      .child(this.userStoreId)
      .on(
        "value",
        action(snap => {
          this.store = snap.val()
          console.log("finished loadStore", this.store)
          this.loading = false
          storefrontStore.reset()
        })
      )

    firebase
      .database()
      .ref("storePurchases")
      .child(this.userStoreId)
      .child("totals")
      .on(
        "value",
        action(totalSnap => {
          var totals = totalSnap.val()
          if (totals) {
            this.storePurchaseTotals.set("numPurchases", totals.numPurchases)
            this.storePurchaseTotals.set("totalVolume", totals.totalVolume)
            this.storePurchaseTotals.set("totalProfit", totals.totalProfit)
          }
        })
      )

    firebase
      .database()
      .ref("storePurchases")
      .child(this.userStoreId)
      .child("products")
      .on(
        "value",
        action(productsSnap => {
          var products = productsSnap.val()
          if (products) {
            Object.keys(products).forEach(productId => {
              this.storePurchaseProducts.set(productId, products[productId])
            })
          }
        })
      )

    console.log("loading the store...")
    this.recentStorePurchases.clear()
    firebase
      .database()
      .ref("storePurchases")
      .child(this.userStoreId)
      .child("purchases")
      .orderByChild("time")
      .startAt(Date.now() - 1000 * 60 * 60 * 24 * 30)
      .on(
        "child_added",
        action(storePurchaseSnap => {
          var purchaseKey = storePurchaseSnap.key
          this.recentStorePurchases.set(purchaseKey, storePurchaseSnap.val())

          firebase
            .database()
            .ref("purchases")
            .child(purchaseKey)
            .once(
              "value",
              action(purchaseSnap => {
                this.purchases.set(purchaseKey, purchaseSnap.val())
              })
            )
        })
      )
  }

  @action.bound
  reserveInitialStoreName(storeName, storeKey) {
    var db = firebase.database()
    var storeNameRef = db.ref("storeNames")
    var storeRef = db.ref("stores")

    var initialStoreName = storeName.replace(/[^A-Za-z0-9]/g, "").toLowerCase()
    if (!initialStoreName) {
      initialStoreName = "store"
    }
    var counter = 1
    findUniqueStoreName()

    function findUniqueStoreName() {
      var currentName = initialStoreName + (counter === 1 ? "" : counter)
      storeNameRef.child(currentName).once("value", function(snapshot) {
        if (snapshot.val()) {
          counter++
          findUniqueStoreName()
        } else {
          storeNameRef.child(currentName).set(storeKey)
          storeRef
            .child(storeKey)
            .child("storeName")
            .set(currentName)
        }
      })
    }
  }

  @action.bound
  createUserAndStore(user, email, storeName) {
    var db = firebase.database()
    var storeRef = db.ref("stores")
    var userRef = db.ref("users")
    var storeKey = storeRef.push().key
    var createdAt = Date.now()

    //1. create user object with store reference
    userRef
      .child(user.uid)
      .child("data")
      .set({
        uid: user.uid,
        email: email,
        createdAt,
      })
      .then(
        userRef
          .child(user.uid)
          .child("stores")
          .child(storeKey)
          .set(true)
      ) //2. create store object
      .then(
        storeRef.child(storeKey).set({
          name: storeName,
          users: {
            [user.uid]: { email: email, access: true },
          },
          createdAt,
        })
      ) //3. create storename object
      .then(this.reserveInitialStoreName(storeName, storeKey))
  }

  @action.bound
  createAccount(email, password, storeName) {
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(user => {
        this.createUserAndStore(user, email, storeName)
      })
      .catch(
        action(err => {
          this.signUpError = err
        })
      )
  }

  @action.bound
  signIn(email, password) {
    firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .catch(
        action(err => {
          this.signInError = err
        })
      )
  }

  @action.bound
  listenToAuth() {
    console.log("listening...")
    this.authenticating = true
    firebase.auth().onAuthStateChanged(
      action(user => {
        this.authenticating = false
        if (user) {
          this.loading = true
          this.loadUser(user)
          this.authenticated = true
        } else {
          this.loading = false
          this.user = null
          this.store = null
          this.authenticated = false
          this.signUpError = null
          this.signInError = null
        }
      })
    )
  }

  @action.bound
  logout() {
    firebase
      .auth()
      .signOut()
      .catch(
        action(err => {
          console.log("logout failure", err)
        })
      )
  }
}

const authStore = new AuthStore()

export default authStore



// WEBPACK FOOTER //
// ./src/auth/auth-store.js